import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Scissors, Plus, Pencil, Trash2, ChevronDown, ChevronUp,
  Package, CheckCircle, Clock, X, Search, RefreshCw, Loader2,
  AlertCircle, TrendingDown, Wallet, ClipboardList, FileText,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { UICtx, AuthCtx } from "../appContexts";
import { ModuleIcon } from "../components/layout/Sidebar";

const STATUS_LABELS = {
  bekliyor: "Bekliyor",
  yapiliyor: "Yapılıyor",
  tamamlandi: "Tamamlandı",
  odendi: "Ödendi",
};

const STATUS_COLORS = {
  bekliyor:    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  yapiliyor:   "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  tamamlandi:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  odendi:      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

// ── Atölye Ekleme/Düzenleme Modalı ───────────────────────────────────────────
function WorkshopModal({ workshop, onClose, onSave }) {
  const [form, setForm] = useState(workshop || { name: "", phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Atölye adı gerekli"); return; }
    setSaving(true);
    try {
      if (workshop) {
        const { data } = await api.put(`/workshops/${workshop.id}`, form);
        onSave(data, "update");
      } else {
        const { data } = await api.post("/workshops", form);
        onSave(data, "create");
      }
      toast.success(workshop ? "Atölye güncellendi" : "Atölye eklendi");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt hatası");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="workshop-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            {workshop ? "Atölye Düzenle" : "Yeni Atölye"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="space-y-3">
          {[
            { key: "name", label: "Atölye Adı *", ph: "örn: Fatma Hanım Atölyesi" },
            { key: "phone", label: "Telefon", ph: "0532 xxx xx xx" },
            { key: "address", label: "Adres", ph: "İl / İlçe" },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">{f.label}</label>
              <input
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder={f.ph}
                value={form[f.key] || ""}
                onChange={e => set(f.key, e.target.value)}
                data-testid={`workshop-input-${f.key}`}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Notlar</label>
            <textarea
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              rows={2} placeholder="İsteğe bağlı notlar"
              value={form.notes || ""}
              onChange={e => set("notes", e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            İptal
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="workshop-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── İş Emri Oluşturma Modalı ──────────────────────────────────────────────────
function AssignmentModal({ workshop, sewingCosts, onClose, onSave }) {
  const [items, setItems] = useState([{ productType: "", unit: "MT", qty: 1, unitPrice: 0 }]);
  const [status, setStatus] = useState("bekliyor");
  const [notes, setNotes]   = useState("");
  const [dueDate, setDueDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [saving, setSaving] = useState(false);
  // Sipariş bağlantısı
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [orderQuery, setOrderQuery] = useState("");
  const [showOrderDrop, setShowOrderDrop] = useState(false);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data || [])).catch(() => {});
  }, []);

  const filteredOrders = (orders || []).filter(o => {
    const q = orderQuery.toLowerCase();
    return !q || (o.orderNo || "").toLowerCase().includes(q) || (o.customerName || "").toLowerCase().includes(q);
  }).slice(0, 15);

  const selectOrder = (o) => {
    setSelectedOrderId(o.id);
    setCustomerName(o.customerName || "");
    setOrderQuery(`${o.orderNo} — ${o.customerName || "?"}`);
    setShowOrderDrop(false);
  };

  const addItem = () => setItems(p => [...p, { productType: "", unit: "MT", qty: 1, unitPrice: 0 }]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const setItemField = (i, k, v) => {
    setItems(p => {
      const n = [...p];
      n[i] = { ...n[i], [k]: v };
      return n;
    });
  };
  const selectSewingCost = (i, sc) => {
    setItemField(i, "productType", sc.productType);
    setItemField(i, "unit", sc.unit);
    setItemField(i, "unitPrice", sc.unitPrice);
  };

  const total = items.reduce((s, it) => s + (parseFloat(it.unitPrice) || 0) * (parseFloat(it.qty) || 0), 0);

  const handleSave = async () => {
    if (!items[0]?.productType) { toast.error("En az bir ürün türü seçin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/workshop-assignments", {
        workshopId: workshop.id,
        workshopName: workshop.name,
        customerName,
        orderId: selectedOrderId || null,
        items: items.map(it => ({ ...it, qty: parseFloat(it.qty), unitPrice: parseFloat(it.unitPrice) })),
        status,
        notes,
        dueDate: dueDate || null,
      });
      onSave(data);
      toast.success("İş emri oluşturuldu");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" data-testid="assignment-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Yeni İş Emri</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{workshop.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Sipariş Seçimi + Müşteri + Tarih */}
        <div className="space-y-3 mb-4">
          {/* Sipariş Arama */}
          <div className="relative">
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Sipariş Bağla (isteğe bağlı)</label>
            <div className="relative">
              <input
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Sipariş no veya müşteri ara…"
                value={orderQuery}
                onChange={e => { setOrderQuery(e.target.value); setShowOrderDrop(true); setSelectedOrderId(""); }}
                onFocus={() => setShowOrderDrop(true)}
                data-testid="assignment-order-search"
              />
              {selectedOrderId && (
                <button onClick={() => { setSelectedOrderId(""); setOrderQuery(""); }}
                  className="absolute right-2 top-2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {showOrderDrop && filteredOrders.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {filteredOrders.map(o => (
                  <button
                    key={o.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-between gap-2 transition-colors"
                    onClick={() => selectOrder(o)}
                  >
                    <span className="font-mono text-xs text-blue-600 dark:text-blue-400 font-bold shrink-0">{o.orderNo}</span>
                    <span className="flex-1 min-w-0 truncate">{o.customerName || "—"}</span>
                    <span className="text-xs text-slate-400 shrink-0">{o.status}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Müşteri Adı</label>
              <input
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="veya otomatik dolar"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Son Teslim</label>
              <input
                type="date"
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Kalemler */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Dikiş Kalemleri</label>
            <button onClick={addItem} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              <Plus className="w-3 h-3" /> Kalem Ekle
            </button>
          </div>
          {items.map((it, i) => (
            <div key={i} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
              <div className="grid grid-cols-12 gap-2 items-start">
                {/* Dikiş türü seçimi */}
                <div className="col-span-6">
                  <select
                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    value={it.productType}
                    onChange={e => {
                      const sc = sewingCosts.find(c => c.productType === e.target.value);
                      if (sc) selectSewingCost(i, sc);
                      else setItemField(i, "productType", e.target.value);
                    }}
                    data-testid={`assignment-item-type-${i}`}
                  >
                    <option value="">— Dikiş Türü Seç —</option>
                    {sewingCosts.map(sc => (
                      <option key={sc.id || sc.code} value={sc.productType}>
                        {sc.code}. {sc.productType} ({sc.unitPrice.toLocaleString("tr-TR")} ₺/{sc.unit})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number" min="0" step="0.5"
                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-right"
                    placeholder="Miktar"
                    value={it.qty}
                    onChange={e => setItemField(i, "qty", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number" min="0"
                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-right"
                    placeholder="Birim ₺"
                    value={it.unitPrice}
                    onChange={e => setItemField(i, "unitPrice", e.target.value)}
                  />
                </div>
                <div className="col-span-1 text-right text-xs font-bold text-slate-700 dark:text-slate-200 pt-2">
                  {((parseFloat(it.unitPrice) || 0) * (parseFloat(it.qty) || 0)).toLocaleString("tr-TR")} ₺
                </div>
                <div className="col-span-1 flex justify-end">
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-1">
            <span className="text-sm font-bold text-slate-900 dark:text-white">
              Toplam: {total.toLocaleString("tr-TR")} ₺
            </span>
          </div>
        </div>

        {/* Durum + Notlar */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Durum</label>
            <select
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Notlar</label>
            <input
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="İsteğe bağlı"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            İptal
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="assignment-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? "Kaydediliyor..." : "İş Emri Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ana Modül ─────────────────────────────────────────────────────────────────
export default function WorkshopsPage() {
  const { theme } = useContext(UICtx);
  const { user }  = useContext(AuthCtx);
  const isAdmin   = user?.role === "admin";

  const [activeTab, setActiveTab]       = useState("workshops");
  const [workshops, setWorkshops]       = useState([]);
  const [assignments, setAssignments]   = useState([]);
  const [sewingCosts, setSewingCosts]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");

  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [editWorkshop, setEditWorkshop]           = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop]   = useState(null);
  const [expandedWs, setExpandedWs]               = useState(new Set());

  const [editingCost, setEditingCost]   = useState(null);
  const [costForm, setCostForm]         = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ws, asgn, sc] = await Promise.all([
        api.get("/workshops"),
        api.get("/workshop-assignments"),
        api.get("/sewing-costs"),
      ]);
      setWorkshops(ws.data || []);
      setAssignments(asgn.data || []);
      setSewingCosts(sc.data || []);
    } catch (e) {
      toast.error("Veriler yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // İlk açılışta dikiş maliyetleri yoksa seed et
  useEffect(() => {
    if (!loading && sewingCosts.length === 0 && isAdmin) {
      api.post("/sewing-costs/seed").then(() => load()).catch(() => {});
    }
  }, [loading, sewingCosts.length, isAdmin, load]);

  const totalDebt = workshops.reduce((s, w) => s + Math.min(w.balance || 0, 0), 0);
  const unpaidAssignments = assignments.filter(a => a.status !== "odendi").length;

  const filteredWorkshops = workshops.filter(w =>
    !search || w.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleExpand = (id) => {
    setExpandedWs(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleDeleteWorkshop = async (ws) => {
    if (!window.confirm(`"${ws.name}" atölyesini silmek istediğinizden emin misiniz?`)) return;
    try {
      await api.delete(`/workshops/${ws.id}`);
      setWorkshops(p => p.filter(w => w.id !== ws.id));
      toast.success("Atölye silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const handleDeleteAssignment = async (wa) => {
    if (!window.confirm("İş emrini silmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/workshop-assignments/${wa.id}`);
      setAssignments(p => p.filter(a => a.id !== wa.id));
      setWorkshops(prev => prev.map(w => w.id === wa.workshopId
        ? { ...w, balance: (w.balance || 0) + wa.totalAmount }
        : w
      ));
      toast.success("İş emri silindi");
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const handleMarkPaid = async (wa) => {
    try {
      const { data } = await api.put(`/workshop-assignments/${wa.id}`, { status: "odendi" });
      setAssignments(p => p.map(a => a.id === wa.id ? data : a));
      setWorkshops(prev => prev.map(w => w.id === wa.workshopId
        ? { ...w, balance: (w.balance || 0) + wa.totalAmount }
        : w
      ));
      toast.success("Ödeme kaydedildi");
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const handleSaveCost = async () => {
    try {
      const { data } = await api.put(`/sewing-costs/${editingCost}`, costForm);
      setSewingCosts(p => p.map(c => c.id === editingCost ? data : c));
      setEditingCost(null);
      toast.success("Fiyat güncellendi");
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const dark = theme === "dark";

  return (
    <div className={`min-h-screen ${dark ? "bg-[#0f1117] text-white" : "bg-[#f1f5f9] text-slate-900"} p-4 sm:p-6`} data-testid="workshops-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <ModuleIcon icon={Scissors} tab="workshops" grad="from-[#f59e0b] via-[#d97706] to-[#92400e]" size="w-11 h-11" iconClass="w-5 h-5" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Atölye & Dikiş İşçiliği</h1>
            <p className={`text-sm mt-0.5 ${dark ? "text-slate-400" : "text-slate-500"}`}>
              Tedarikçi borç takibi ve dikiş maliyet matrisi
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className={`p-2 rounded-xl border ${dark ? "border-slate-700 hover:bg-slate-800" : "border-slate-200 hover:bg-white"} transition-colors`}
            title="Yenile"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {isAdmin && activeTab === "workshops" && (
            <button
              onClick={() => { setEditWorkshop(null); setShowWorkshopModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl transition-colors"
              data-testid="add-workshop-btn"
            >
              <Plus className="w-4 h-4" /> Atölye Ekle
            </button>
          )}
        </div>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Toplam Atölye", value: workshops.length, icon: Scissors, color: "text-amber-600" },
          { label: "Bekleyen İş Emri", value: unpaidAssignments, icon: ClipboardList, color: "text-blue-600" },
          { label: "Toplam Borç", value: `${Math.abs(totalDebt).toLocaleString("tr-TR")} ₺`, icon: TrendingDown, color: "text-red-500" },
          { label: "Dikiş Türü", value: sewingCosts.length, icon: FileText, color: "text-emerald-600" },
        ].map(c => (
          <div key={c.label} className={`rounded-2xl p-4 ${dark ? "bg-slate-800/60 border border-slate-700/50" : "bg-white border border-slate-100"} shadow-sm`}>
            <c.icon className={`w-5 h-5 ${c.color} mb-2`} />
            <div className="text-xl font-bold">{c.value}</div>
            <div className={`text-xs mt-0.5 ${dark ? "text-slate-400" : "text-slate-500"}`}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Sekmeler */}
      <div className={`flex gap-1 mb-5 p-1 rounded-xl w-fit ${dark ? "bg-slate-800" : "bg-slate-200"}`}>
        {[
          { id: "workshops", label: "Atölyeler", icon: Scissors },
          { id: "matrix", label: "Dikiş Fiyat Matrisi", icon: FileText },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`workshops-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === t.id
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                : `${dark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-700"}`
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 gap-2 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
        </div>
      ) : (
        <>
          {/* ── Atölyeler Sekmesi ─────────────────────────────────────────── */}
          {activeTab === "workshops" && (
            <div>
              {/* Arama */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-4 w-full max-w-sm ${dark ? "bg-slate-800 border border-slate-700" : "bg-white border border-slate-200"}`}>
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  className="bg-transparent text-sm outline-none flex-1"
                  placeholder="Atölye ara..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="workshop-search"
                />
              </div>

              {filteredWorkshops.length === 0 ? (
                <div className={`rounded-2xl p-12 text-center ${dark ? "bg-slate-800/50 border border-slate-700/50" : "bg-white border border-slate-100"}`}>
                  <Scissors className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium">Henüz atölye eklenmedi</p>
                  {isAdmin && (
                    <button onClick={() => { setEditWorkshop(null); setShowWorkshopModal(true); }} className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-semibold">
                      + İlk atölyeyi ekle
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredWorkshops.map(ws => {
                    const wsAssignments = assignments.filter(a => a.workshopId === ws.id);
                    const isExpanded = expandedWs.has(ws.id);
                    const debt = ws.balance || 0;
                    return (
                      <div
                        key={ws.id}
                        className={`rounded-2xl border ${dark ? "bg-slate-800/60 border-slate-700/50" : "bg-white border-slate-100"} shadow-sm overflow-hidden`}
                        data-testid={`workshop-card-${ws.id}`}
                      >
                        {/* Atölye başlığı */}
                        <div className="flex items-center justify-between p-4 gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-base">{ws.name}</span>
                              {ws.phone && (
                                <span className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{ws.phone}</span>
                              )}
                            </div>
                            {ws.address && (
                              <p className={`text-xs mt-0.5 ${dark ? "text-slate-500" : "text-slate-400"}`}>{ws.address}</p>
                            )}
                          </div>
                          {/* Bakiye */}
                          <div className={`text-right ${debt < 0 ? "text-red-500" : "text-emerald-600"}`}>
                            <div className="text-lg font-bold">{Math.abs(debt).toLocaleString("tr-TR")} ₺</div>
                            <div className="text-xs font-medium">{debt < 0 ? "Borç" : "Alacak"}</div>
                          </div>
                          {/* Aksiyonlar */}
                          <div className="flex items-center gap-1.5 shrink-0">
                            {isAdmin && (
                              <button
                                onClick={() => { setSelectedWorkshop(ws); setShowAssignmentModal(true); }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1"
                                data-testid={`ws-new-assignment-${ws.id}`}
                              >
                                <Plus className="w-3 h-3" /> İş Emri
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => { setEditWorkshop(ws); setShowWorkshopModal(true); }}
                                className={`p-2 rounded-lg ${dark ? "hover:bg-slate-700" : "hover:bg-slate-100"} transition-colors`}
                              >
                                <Pencil className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteWorkshop(ws)}
                                className={`p-2 rounded-lg ${dark ? "hover:bg-slate-700" : "hover:bg-slate-100"} transition-colors`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            )}
                            <button
                              onClick={() => toggleExpand(ws.id)}
                              className={`p-2 rounded-lg ${dark ? "hover:bg-slate-700" : "hover:bg-slate-100"} transition-colors`}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            </button>
                          </div>
                        </div>

                        {/* İş emirleri (genişletilmiş) */}
                        {isExpanded && (
                          <div className={`border-t ${dark ? "border-slate-700/50" : "border-slate-100"} p-4`}>
                            {wsAssignments.length === 0 ? (
                              <p className={`text-sm ${dark ? "text-slate-500" : "text-slate-400"} text-center py-4`}>
                                Bu atölye için iş emri yok
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {wsAssignments.map(a => (
                                  <div
                                    key={a.id}
                                    className={`flex items-center justify-between gap-3 rounded-xl p-3 ${dark ? "bg-slate-900/60" : "bg-slate-50"}`}
                                    data-testid={`assignment-row-${a.id}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-mono font-bold text-slate-400">{a.id}</span>
                                        {a.customerName && <span className="text-sm font-semibold">{a.customerName}</span>}
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[a.status] || "bg-slate-100 text-slate-600"}`}>
                                          {STATUS_LABELS[a.status] || a.status}
                                        </span>
                                      </div>
                                      <div className={`text-xs mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>
                                        {(a.items || []).slice(0, 2).map((it, ii) => (
                                          <span key={ii} className="mr-2">{it.productType} × {it.qty} {it.unit}</span>
                                        ))}
                                        {a.items?.length > 2 && <span>+{a.items.length - 2} daha</span>}
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <div className="font-bold text-sm">{(a.totalAmount || 0).toLocaleString("tr-TR")} ₺</div>
                                      {a.dueDate && <div className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{a.dueDate}</div>}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                      {a.status !== "odendi" && isAdmin && (
                                        <button
                                          onClick={() => handleMarkPaid(a)}
                                          className="p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
                                          title="Ödendi olarak işaretle"
                                          data-testid={`assignment-paid-${a.id}`}
                                        >
                                          <CheckCircle className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      {isAdmin && (
                                        <button
                                          onClick={() => handleDeleteAssignment(a)}
                                          className="p-1.5 rounded-lg hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Dikiş Fiyat Matrisi Sekmesi ───────────────────────────────── */}
          {activeTab === "matrix" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
                  Eylül Perde 2026 dikiş fiyat listesi — {sewingCosts.length} kalem
                </p>
                {isAdmin && sewingCosts.length === 0 && (
                  <button
                    onClick={() => api.post("/sewing-costs/seed").then(load).catch(() => toast.error("Yüklenemedi"))}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-1.5"
                    data-testid="seed-costs-btn"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Fiyat Listesini Yükle
                  </button>
                )}
              </div>

              <div className={`rounded-2xl overflow-hidden border ${dark ? "border-slate-700/50" : "border-slate-100"} shadow-sm`}>
                <table className="w-full">
                  <thead>
                    <tr className={`${dark ? "bg-slate-800" : "bg-slate-50"} text-xs font-semibold uppercase tracking-wide`}>
                      <th className="px-4 py-3 text-left w-10 text-slate-400">#</th>
                      <th className="px-4 py-3 text-left text-slate-500 dark:text-slate-400">Dikiş Türü</th>
                      <th className="px-4 py-3 text-center w-20 text-slate-500 dark:text-slate-400">Birim</th>
                      <th className="px-4 py-3 text-right w-32 text-slate-500 dark:text-slate-400">Birim Fiyat</th>
                      {isAdmin && <th className="px-4 py-3 text-right w-20 text-slate-400"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sewingCosts.map((sc, idx) => (
                      <tr
                        key={sc.id || sc.code}
                        className={`border-t ${dark ? "border-slate-700/30 hover:bg-slate-800/40" : "border-slate-50 hover:bg-slate-50"} transition-colors`}
                        data-testid={`sewing-cost-row-${sc.code}`}
                      >
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-400">{sc.code}</td>
                        <td className="px-4 py-2.5 text-sm font-medium">
                          {editingCost === sc.id ? (
                            <input
                              className={`w-full bg-transparent border-b ${dark ? "border-blue-500" : "border-blue-400"} outline-none text-sm`}
                              value={costForm.productType ?? sc.productType}
                              onChange={e => setCostForm(p => ({ ...p, productType: e.target.value }))}
                            />
                          ) : sc.productType}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${dark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                            {sc.unit}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-bold text-amber-600 dark:text-amber-400">
                          {editingCost === sc.id ? (
                            <input
                              type="number"
                              className={`w-24 bg-transparent border-b ${dark ? "border-blue-500" : "border-blue-400"} outline-none text-sm text-right`}
                              value={costForm.unitPrice ?? sc.unitPrice}
                              onChange={e => setCostForm(p => ({ ...p, unitPrice: parseFloat(e.target.value) }))}
                            />
                          ) : `${sc.unitPrice.toLocaleString("tr-TR")} ₺`}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-2.5 text-right">
                            {editingCost === sc.id ? (
                              <div className="flex justify-end gap-1">
                                <button onClick={handleSaveCost} className="p-1 text-emerald-500 hover:text-emerald-600" title="Kaydet">
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditingCost(null)} className="p-1 text-slate-400 hover:text-slate-600" title="İptal">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setEditingCost(sc.id); setCostForm({}); }}
                                className={`p-1.5 rounded-lg ${dark ? "hover:bg-slate-700" : "hover:bg-slate-100"} transition-colors`}
                                data-testid={`edit-cost-${sc.code}`}
                              >
                                <Pencil className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modaller */}
      {showWorkshopModal && (
        <WorkshopModal
          workshop={editWorkshop}
          onClose={() => { setShowWorkshopModal(false); setEditWorkshop(null); }}
          onSave={(data, type) => {
            if (type === "create") setWorkshops(p => [...p, data]);
            else setWorkshops(p => p.map(w => w.id === data.id ? data : w));
          }}
        />
      )}
      {showAssignmentModal && selectedWorkshop && (
        <AssignmentModal
          workshop={selectedWorkshop}
          sewingCosts={sewingCosts}
          onClose={() => { setShowAssignmentModal(false); setSelectedWorkshop(null); }}
          onSave={(data) => {
            setAssignments(p => [data, ...p]);
            setWorkshops(prev => prev.map(w => w.id === data.workshopId
              ? { ...w, balance: (w.balance || 0) - data.totalAmount }
              : w
            ));
          }}
        />
      )}
    </div>
  );
}
